import React, { createContext, useContext, ReactNode, useCallback, useMemo, useEffect, useState } from 'react';
import { useUpdateUserSettings } from 'shared/hooks/useUpdateUserSettings';
import useClinicUser from 'shared/hooks/useClinicUser';
import { addMinutes, addDays, setHours, setMinutes, isToday, isTomorrow } from 'date-fns';
import { utcToZonedTime, format } from 'date-fns-tz';
import { useTracking } from 'shared/providers/TrackingProvider';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useDisclosure } from '@televet/kibble-ui/build/chakra';
import { CustomSnoozeModalEvents } from '../TrackingProvider/EventNames';

type CustomSnoozeInput = {
  date: string;
  time: string;
};

interface ISnoozeProvider {
  timeZone: string;
  isSnoozed: boolean;
  snoozedUntil: Date;
  isCustomSnoozeModalOpen: boolean;
  turnOffSnooze: () => void;
  handleTomorrowClick: () => void;
  handleTimeClick: (minutes: number) => void;
  getSnoozeDescription: () => string | undefined;
  saveCustomSnooze: (values: CustomSnoozeInput) => Promise<void>;
  onCustomSnoozeModalOpen: () => void;
  onCustomSnoozeModalClose: () => void;
}

const SnoozeContext = createContext<ISnoozeProvider>({
  timeZone: '',
  snoozedUntil: utcToZonedTime(new Date(), ''),
  isSnoozed: false,
  isCustomSnoozeModalOpen: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  saveCustomSnooze: (values: CustomSnoozeInput): Promise<void> => {
    throw new Error('saveCustomSnooze(values: CustomSnoozeInput): void not implemented');
  },
  handleTimeClick: (): void => {
    throw new Error('handleTimeClick(): void not implemented');
  },
  handleTomorrowClick: (): void => {
    throw new Error('handleTomorrowClick(): void not implemented');
  },
  getSnoozeDescription: (): string | undefined => {
    throw new Error('handleTomorrowClick(): void not implemented');
  },
  turnOffSnooze: (): void => {
    throw new Error('handleTomorrowClick(): void not implemented');
  },
  onCustomSnoozeModalOpen: (): void => {
    throw new Error('onCustomSnoozeModalOpen(): void not implemented');
  },
  onCustomSnoozeModalClose: (): void => {
    throw new Error('onCustomSnoozeModalClose(): void not implemented');
  },
});

interface SnoozeProviderProps {
  children: ReactNode;
}

export const useSnoozeProvider = (): ISnoozeProvider => useContext<ISnoozeProvider>(SnoozeContext);

const ONE_MINUTES_IN_MS = 1000 * 60;

const SnoozeProvider = ({ children }: SnoozeProviderProps): JSX.Element => {
  const { update } = useUpdateUserSettings();
  const { clinicUser } = useClinicUser();
  const { trackEvent } = useTracking();
  const toast = useToast();
  const {
    isOpen: isCustomSnoozeModalOpen,
    onOpen: onCustomSnoozeModalOpen,
    onClose: onCustomSnoozeModalClose,
  } = useDisclosure();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const snoozedUntil = useMemo(
    () =>
      clinicUser?.userSetting?.snoozeNotificationsUntil
        ? utcToZonedTime(new Date(clinicUser?.userSetting?.snoozeNotificationsUntil), timeZone)
        : utcToZonedTime(new Date(), timeZone),
    [clinicUser, timeZone],
  );

  const [isSnoozed, setIsSnoozed] = useState<boolean>(snoozedUntil ? snoozedUntil > new Date() : false);

  /**
   * Check once a minute if we are snoozed
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setIsSnoozed(snoozedUntil ? snoozedUntil > new Date() : false);
    }, ONE_MINUTES_IN_MS);
    return (): void => clearInterval(interval);
  }, [snoozedUntil]);

  const saveCustomSnooze = useCallback(
    async (values: CustomSnoozeInput): Promise<void> => {
      const { date, time } = values;
      const dateToSnooze = new Date(`${date} ${time}`);
      if (dateToSnooze <= new Date()) {
        toast({
          status: 'error',
          title: `Please select a future date and time`,
        });
        return;
      }
      await update({ snoozeNotificationsUntil: dateToSnooze.toISOString() });
      setIsSnoozed(true);
      onCustomSnoozeModalClose();
    },
    [update, onCustomSnoozeModalClose, toast],
  );

  const handleTimeClick = (minutes: number): void => {
    const enumJSON: { [key: number]: string } = {
      30: '30 Minutes',
      60: '1 Hour',
      120: '2 Hours',
      180: '3 Hours',
    };

    trackEvent(CustomSnoozeModalEvents.EditSnoozeAmount, { minutes: enumJSON[minutes] });

    update({
      snoozeNotificationsUntil: addMinutes(new Date(), minutes).toISOString(),
    });
    setIsSnoozed(true);
  };

  const handleTomorrowClick = (): void => {
    trackEvent(CustomSnoozeModalEvents.EditSnoozeAmount, { until: 'Tomorrow at 8 am' });

    update({
      snoozeNotificationsUntil: setMinutes(setHours(addDays(new Date(), 1), 8), 0).toISOString(),
    });
  };

  const getSnoozeDescription = useCallback(() => {
    if (!snoozedUntil) return;
    else {
      if (isToday(snoozedUntil)) {
        return format(snoozedUntil, 'h:mm a z', { timeZone });
      } else if (isTomorrow(snoozedUntil)) {
        return format(snoozedUntil, "'tomorrow at' h:mm a z", { timeZone });
      } else {
        return format(snoozedUntil, "MMM do 'at' h:mm a z", { timeZone });
      }
    }
  }, [snoozedUntil, timeZone]);

  const turnOffSnooze = (): void => {
    trackEvent(CustomSnoozeModalEvents.TurnOffSnooze);

    update({
      snoozeNotificationsUntil: new Date().toISOString(),
    });
    setIsSnoozed(false);
  };

  return (
    <SnoozeContext.Provider
      value={{
        timeZone,
        snoozedUntil,
        isSnoozed,
        saveCustomSnooze,
        handleTimeClick,
        handleTomorrowClick,
        getSnoozeDescription,
        turnOffSnooze,
        isCustomSnoozeModalOpen,
        onCustomSnoozeModalOpen: (): void => {
          trackEvent(CustomSnoozeModalEvents.ViewSnoozeModal);
          onCustomSnoozeModalOpen();
        },
        onCustomSnoozeModalClose,
      }}
    >
      {children}
    </SnoozeContext.Provider>
  );
};

export default SnoozeProvider;
